import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Search, Download, AlertCircle, FileText, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ohipTransactionsService from '../services/OhipTransactionsService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ohipHotelGroupsService from '../services/OhipHotelGroupsService';
import InvoiceWizard from './InvoiceWizard';
import MultiSelectSearch from './MultiSelectSearch';
import './OhipTransactionsPage.css';

// Admin > Billing > OHIP Transactions.
//
// THE WORD IS ENVIRONMENT, NOT GROUP. `group` survives as a field name because it is what the OpenSearch
// documents and the sort whitelist are keyed on — group_key is the join to two years of history — but
// nothing a reader sees says "group".
//
// Reads the ohip-transactions alias — one index per day, two-year retention. Filters, a per-day chart,
// and a paged detail table of group x hotel x day.
//
// The whole screen is driven by ONE filter object. Summary and rows are fetched with the same params, so
// the header totals always describe exactly the rows below them; computing totals from the current page
// would report the page, not the selection, and the two would silently disagree once paging started.

const PAGE_SIZE = 100;

// Dates are yyyy-mm-dd everywhere in this portal, and the API takes the same, so no formatting layer is
// needed between the picker and the query — the value the user sees is the value sent.
const isoDay = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - n);
    return isoDay(d);
};

// environmentIds is a LIST, and empty means every environment. A tenancy's billing is routinely read
// several environments at a time — a group runs more than one — and answering that with one query beats
// running the report repeatedly and adding the totals up by hand.
// hotelKeys is a list too: 'h:<code>' for one hotel, 'c:<chainId>' for every hotel of a chain. Empty means all.
const EMPTY_FILTERS = { from: daysAgo(30), to: isoDay(new Date()), hotelKeys: [], environmentIds: [], failedOnly: false };

// THE HOTEL PICKER SEARCHES BY NAME AND CHAIN, not only by OHIP code. A vendor knows its hotels by name and
// its customers by chain; "CMRU" means nothing to it. Each code carries its linked hub hotel and chain
// (ohip_hotel_links, from the filters view); a code nobody has linked is offered by its code alone.
// The label is what MultiSelectSearch matches on, so name, code and chain all go into it.
const hotelPickerOptions = (codes, labels) => {
    const byCode = new Map((labels || []).map(l => [l.code, l]));
    const chains = new Map();
    const hotels = codes.map(code => {
        const l = byCode.get(code) || {};
        if (l.chainId != null) {
            const c = chains.get(l.chainId) || { name: l.chainName || `Chain ${l.chainId}`, count: 0 };
            c.count += 1;
            chains.set(l.chainId, c);
        }
        const name = l.hotelName ? `${l.hotelName} (${code})` : code;
        return { value: `h:${code}`, label: l.chainName ? `${name} · ${l.chainName}` : name, sort: (l.hotelName || code).toLowerCase() };
    }).sort((a, b) => a.sort.localeCompare(b.sort));
    const chainOpts = [...chains.entries()]
        .map(([id, c]) => ({ value: `c:${id}`, label: `Chain: ${c.name} — all ${c.count} hotel${c.count === 1 ? '' : 's'}`, sort: c.name.toLowerCase() }))
        .sort((a, b) => a.sort.localeCompare(b.sort));
    return [...chainOpts, ...hotels];
};

// The OHIP codes a hotelKeys selection stands for.
const codesOfKeys = (keys, labels) => {
    const codes = new Set();
    for (const k of keys || []) {
        if (k.startsWith('h:')) codes.add(k.slice(2));
        else if (k.startsWith('c:')) {
            const chainId = Number(k.slice(2));
            for (const l of labels || []) if (l.chainId === chainId) codes.add(l.code);
        }
    }
    return [...codes];
};

// ── Period shortcuts ─────────────────────────────────────────────────────────
// CALENDAR PERIODS, NOT ROLLING WINDOWS. "Last month" is 1–31 August, not the last 30 days, because this
// screen is what invoices are raised from and an invoice covers a calendar month. A rolling 30 days would
// straddle two of them and quietly disagree with every figure on the Invoices screen — and the Generate
// invoice button takes its month from `from`, so a rolling window would target the wrong one.
//
// UTC throughout, matching the indexed `date` (a UTC day). Deriving these from local time would put the
// 1st of the month in the previous period for anyone west of Greenwich.
const utcDay = (y, m, d) => isoDay(new Date(Date.UTC(y, m, d)));

const PERIODS = [
    {
        key: 'month',
        label: 'Last month',
        range: () => {
            const n = new Date();
            const y = n.getUTCFullYear();
            const m = n.getUTCMonth();
            // Day 0 of month m is the last day of month m-1 — and it rolls the year correctly in January.
            return { from: utcDay(y, m - 1, 1), to: utcDay(y, m, 0) };
        },
    },
    {
        key: 'quarter',
        label: 'Last quarter',
        range: () => {
            const n = new Date();
            const y = n.getUTCFullYear();
            const q = Math.floor(n.getUTCMonth() / 3);      // 0-3, the quarter we are IN
            const startMonth = (q - 1) * 3;                 // negative in Q1; Date.UTC rolls the year back
            return { from: utcDay(y, startMonth, 1), to: utcDay(y, startMonth + 3, 0) };
        },
    },
    {
        key: 'year',
        label: 'Last year',
        range: () => {
            const y = new Date().getUTCFullYear();
            return { from: utcDay(y - 1, 0, 1), to: utcDay(y - 1, 11, 31) };
        },
    },
];

const num = (n) => (n == null ? '—' : Number(n).toLocaleString());

// `vendorId` — VENDOR SETTINGS > OHIP TRANSACTIONS. The same screen, narrowed by the server to the hotel
// codes that vendor reaches (linked on Link hotels to a hotel it was granted). Everything that is billing
// administration rather than reading — Link hotels, the environment lookups, Generate invoice — is the
// superadmin's and is left out; `embedded` drops the page shell so it sits inside a tab.
function OhipTransactionsPage({ vendorId = null, embedded = false }) {
    const scoped = vendorId != null;
    const navigate = useNavigate();
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // `applied` is what the last query actually used. Editing a filter must not re-query on every
    // keystroke — the user presses Search — but the results must describe the applied set, not the
    // half-typed one, so the two are tracked separately.
    const [applied, setApplied] = useState(EMPTY_FILTERS);

    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ field: 'date', order: 'desc' });

    // The hotel codes that actually appear in the billing data. Groups are NOT a filter on this screen:
    // a group is a reporting bundle, not a thing anyone searches by, and the column already shows which
    // one a row came from.
    const [options, setOptions] = useState({ hotels: [], hotelLabels: [] });
    // hotel_code -> environment, and the environments with their member codes. From the
    // hotel-groups lambda: this one has no database connection of its own.
    const [envs, setEnvs] = useState({ codes: [], environments: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [invoicing, setInvoicing] = useState(null);

    const queryParams = useMemo(() => {
        const p = { from: applied.from, to: applied.to };
        if (scoped) p.vendorId = vendorId;
        if (applied.failedOnly) p.failedOnly = 'true';

        // ENVIRONMENT IS EXPANDED TO HOTEL CODES, not sent as a field of its own.
        //
        // The billing documents carry hotelId and no environment — 232 days of history were indexed
        // before the concept existed, and stamping it on would leave all of that unlabelled unless every
        // day were re-indexed. Since the API already filters on a list of hotel codes, an environment is
        // simply the set of codes that belong to it, and the whole archive answers correctly.
        //
        // An explicit Hotel choice wins: it is the narrower of the two, and a user who picked one hotel
        // did not mean "and everything else in its tenancy".
        if ((applied.hotelKeys || []).length) {
            const codes = codesOfKeys(applied.hotelKeys, options.hotelLabels);
            p.hotelId = codes.length ? codes.join(',') : '__none__';
        } else if ((applied.environmentIds || []).length) {
            // The UNION of the chosen environments' codes. Deduplicated, because one property can appear
            // in two environments and sending its code twice would not be wrong, just longer.
            const chosen = new Set(applied.environmentIds);
            const codes = new Set();
            for (const env of (envs.environments || [])) {
                if (!chosen.has(env.environmentId)) continue;
                for (const c of (env.hotelCodes || [])) codes.add(c);
            }
            // Only codes that actually appear in the billing data — sending all 46 of an environment's
            // properties when 3 have traffic is a longer query for the same answer.
            const known = new Set(options.hotels || []);
            const wanted = [...codes].filter(c => known.has(c));
            // No overlap means these environments have no billing data in range. Send an impossible code
            // rather than nothing at all: dropping the filter would silently show EVERY hotel.
            p.hotelId = wanted.length ? wanted.join(',') : '__none__';
        }
        return p;
    }, [applied, envs, options.hotels, options.hotelLabels, scoped, vendorId]);

    useEffect(() => {
        ohipTransactionsService.filters(scoped ? { vendorId } : undefined)
            .then(res => setOptions({ hotels: (res.data && res.data.hotels) || [], hotelLabels: (res.data && res.data.hotelLabels) || [] }))
            .catch(() => setOptions({ hotels: [], hotelLabels: [] })); // the picker degrades to "All hotels"
    }, [scoped, vendorId]);

    useEffect(() => {
        // Admin-only (the hotel-groups lambda is on the admin API). Scoped, the rows still name their
        // environment (`group`); only the Environment filter and the tenancy badge go quiet.
        if (scoped) return;
        ohipHotelGroupsService.environments()
            .then(res => setEnvs(res.data || { codes: [], environments: [] }))
            // The screen works without it; only the Environment filter and column go quiet.
            .catch(() => setEnvs({ codes: [], environments: [] }));
    }, [scoped]);

    // The Hotel dropdown is SCOPED TO THE SELECTED ENVIRONMENT.
    //
    // Offering all 91 codes under an environment holding three of them invites a selection that returns
    // nothing — the two filters are ANDed, so picking BAWE under "USC Hotel (mtcu6pr) SCHLTL1" is an
    // empty result the screen cannot explain. Intersected with the codes that actually appear in the
    // billing data for the same reason the expansion is: an environment's other properties have no rows
    // to find.
    //
    // Driven by `filters`, not `applied`: choosing an environment must narrow the list next to it
    // immediately, before Search is pressed.
    const hotelCodesInEnvs = useMemo(() => {
        if (!(filters.environmentIds || []).length) return options.hotels || [];
        const chosen = new Set(filters.environmentIds);
        const inEnv = new Set();
        for (const env of (envs.environments || [])) {
            if (!chosen.has(env.environmentId)) continue;
            for (const c of (env.hotelCodes || [])) inEnv.add(c);
        }
        if (!inEnv.size) return options.hotels || [];
        return (options.hotels || []).filter(h => inEnv.has(h));
    }, [filters.environmentIds, envs, options.hotels]);
    // Chains count only the hotels left in that list, so "all 3 hotels" is the three the query will cover.
    const hotelOptions = useMemo(() => hotelPickerOptions(hotelCodesInEnvs, (options.hotelLabels || [])
        .filter(l => hotelCodesInEnvs.includes(l.code))), [hotelCodesInEnvs, options.hotelLabels]);

    // hotel code -> its label, for naming the hotel on each row.
    const labelByCode = useMemo(() => new Map((options.hotelLabels || []).map(l => [l.code, l])), [options.hotelLabels]);

    // The environments as the picker wants them. Sorted by name for the same reason the old <select>
    // was: ninety of them, and nobody knows an environment by its id.
    const envOptions = useMemo(() => (envs.environments || [])
        .map(e => ({ value: e.environmentId, label: e.environmentName }))
        .sort((a, b) => a.label.localeCompare(b.label)), [envs]);

    // hotel code -> environment, for labelling each row.
    const envByCode = useMemo(() => {
        const m = new Map();
        for (const c of (envs.codes || [])) m.set(c.hotelCode, c);
        return m;
    }, [envs]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, r] = await Promise.all([
                ohipTransactionsService.summary(queryParams),
                ohipTransactionsService.rows({
                    ...queryParams,
                    size: PAGE_SIZE,
                    from_: page * PAGE_SIZE,
                    sort: sort.field,
                    order: sort.order,
                }),
            ]);
            setSummary(s.data || null);
            setRows((r.data && r.data.rows) || []);
            setTotal((r.data && r.data.total) || 0);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to load OHIP transactions');
            setSummary(null);
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [queryParams, page, sort]);

    useEffect(() => { load(); }, [load]);

    // FILTERS APPLY THEMSELVES. `applied` still exists — the results must always describe a settled filter
    // set, never a half-typed one — but waiting for a button press meant the header could show 2.7m
    // requests over "30 days" while the bar read 02/09 → 07/09. A screen whose numbers disagree with the
    // controls above them is worse than a slow one: nothing on it says which of the two is true.
    //
    // DEBOUNCED, because a native date input emits on every field a user tabs through, and each apply is
    // two OpenSearch queries. 400ms is below the threshold where it feels laggy and above the gap between
    // day/month/year edits.
    //
    // Empty dates are ignored rather than queried. Clearing `from` mid-edit would otherwise ask for
    // everything since the epoch, which is slow and is never what anybody meant.
    const sameFilters = (a, b) =>
        a.from === b.from && a.to === b.to
        && (a.hotelKeys || []).join(',') === (b.hotelKeys || []).join(',')
        // Joined rather than compared element by element: the list is short, always built the same way,
        // and a length-then-index comparison here is three lines that can only ever agree with this one.
        && (a.environmentIds || []).join(',') === (b.environmentIds || []).join(',')
        && a.failedOnly === b.failedOnly;

    useEffect(() => {
        if (!filters.from || !filters.to) return undefined;
        if (sameFilters(filters, applied)) return undefined;
        const t = setTimeout(() => {
            setPage(0);      // a new filter set invalidates the current offset
            setApplied(filters);
        }, 400);
        return () => clearTimeout(t);
    }, [filters, applied]);

    // Search stays, and is not redundant: it applies immediately rather than after the debounce, and it is
    // the affordance that tells a reader the filters are a query rather than a view.
    const search = () => {
        setPage(0);
        setApplied(filters);
    };

    // True while the figures on screen belong to a filter set that is no longer the one in the bar. Used
    // to dim them, so a reader never reads a stale number as a current one.
    const stale = loading || !sameFilters(filters, applied);

    const toggleSort = (field) => {
        setPage(0);
        setSort(s => (s.field === field ? { field, order: s.order === 'asc' ? 'desc' : 'asc' } : { field, order: 'desc' }));
    };

    // Export what is on screen. Deliberately the CURRENT PAGE only, and the button says so — silently
    // exporting a different set from the one displayed is the kind of thing that ends up in a customer
    // invoice dispute.
    const exportCsv = () => {
        const head = ['date', 'group', 'hotelId', 'hotelName', 'chain', 'totalRequests', 'successRequests', 'failedRequests'];
        const csv = (v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : v);
        const body = rows.map(r => {
            const l = labelByCode.get(r.hotelId) || {};
            const row = { ...r, hotelName: l.hotelName || '', chain: l.chainName || '' };
            return head.map(h => csv(row[h] ?? '')).join(',');
        });
        const blob = new Blob([[head.join(','), ...body].join('\n')], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `ohip-transactions-${applied.from}_${applied.to}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    // The real busiest day (0 when there is none), and the value the bars are scaled by — never 0, so nothing
    // divides by it. Showing the second as the peak printed "peak 1" and a 1/1/1 axis over an empty range (HIL-131).
    const peakDay = useMemo(
        () => Math.max(0, ...((summary?.byDay || []).map(d => d.totalRequests || 0))),
        [summary],
    );
    const maxDay = Math.max(1, peakDay);
    const hasData = peakDay > 0;

    // THE AXIS TOPS OUT ABOVE THE PEAK, at a round number.
    //
    // Bars used to be measured against maxDay itself, which put the tallest bar flush with the ceiling
    // and left the scale implicit — a reader could see that one day was twice another but not what
    // either was worth. Rounding up to 1, 2 or 5 x 10^n gives ticks a person can read off (0, 40k, 80k,
    // 120k, 160k) instead of quarters of an arbitrary 153,717.
    // Four intervals plus zero: five gridlines, enough to read a value off and few enough not to crowd
    // 140px. Rounding the INTERVAL rather than the ceiling is what keeps the chart full — rounding the
    // ceiling put a 153,717 peak under a 200,000 axis and threw away a quarter of the height, where a
    // 40,000 interval tops out at 160,000 and fills 96% of it.
    const axis = useMemo(() => {
        // At least 1: requests are whole numbers, and a fractional interval labels 0.25, 0.5 … as "0, 1, 1, 1".
        const raw = Math.max(1, maxDay / 4);
        const pow = Math.pow(10, Math.floor(Math.log10(raw)));
        const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find(m => m * pow >= raw) || 10;
        const interval = step * pow;
        const top = interval * 4;
        return { top, ticks: [4, 3, 2, 1, 0].map(i => interval * i) };
    }, [maxDay]);

    // 153,717 -> "154k". The exact peak is already stated in the header, so the axis can afford to be
    // legible rather than precise — a five-digit number repeated five times down the side is neither.
    const tick = (n) => {
        if (n >= 1e9) return `${+(n / 1e9).toFixed(1)}bn`;
        if (n >= 1e6) return `${+(n / 1e6).toFixed(1)}m`;
        // Below 10k a whole number of thousands collapses distinct ticks into the same label — 1,500 and
        // 2,000 both showed as "2k", which is worse than no axis at all.
        if (n >= 1e4) return `${Math.round(n / 1e3)}k`;
        if (n >= 1e3) return `${+(n / 1e3).toFixed(1)}k`;
        return String(Math.round(n));
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <div className={embedded ? 'otx-embedded' : 'otx-page'}>
            <div className="otx-header">
                <div>
                    {embedded ? <h2>OHIP Transactions</h2> : <h1>OHIP Transactions</h1>}
                    <p className="otx-subtitle">
                        {scoped
                            ? 'OHIP API usage for the hotels you have been given access to, per day. Sourced from the '
                              + 'Oracle Hospitality Developer Portal and retained for 2 years.'
                            : 'API usage per environment, per day. Sourced from the Oracle Hospitality Developer '
                              + 'Portal and retained for 2 years.'}
                    </p>
                </div>
                <div className="otx-header-actions">
                    {/* Link OHIP property codes to hub hotels and set each billing group's bill-to. */}
                    {!scoped && (
                        <Button variant="secondary" onClick={() => navigate('/admin/billing/ohip-transactions/link-hotels')}>
                            <Link2 className="icon" /> Link hotels
                        </Button>
                    )}
                    <Button variant="secondary" onClick={exportCsv} disabled={!rows.length}>
                        <Download className="icon" /> Export page
                    </Button>
                    <Button variant="success" onClick={load}>
                        <RefreshCw className="icon" /> Refresh
                    </Button>
                </div>
            </div>

            <div className="otx-filters">
                <label>
                    <span>From</span>
                    <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
                </label>
                <label>
                    <span>To</span>
                    <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
                </label>
                {/* Shortcuts, not a mode: they write into the same two date fields, so what they selected
                    stays visible and editable rather than becoming a hidden state the reader cannot
                    adjust. The active one is derived by comparing the dates, so hand-typing August's
                    range lights "Last month" too — the button reflects the filter, not the last click. */}
                <div className="otx-periods">
                    {PERIODS.map((p) => {
                        const r = p.range();
                        const on = filters.from === r.from && filters.to === r.to;
                        return (
                            <button
                                key={p.key}
                                type="button"
                                className={`otx-period${on ? ' otx-period-on' : ''}`}
                                title={`${r.from} → ${r.to}`}
                                onClick={() => setFilters(f => ({ ...f, ...r }))}
                            >
                                {p.label}
                            </button>
                        );
                    })}
                </div>
                {/* CHIPS AND A SEARCH BOX, not a <select multiple>. There are ninety-odd environments
                    and the names are long; a native multi-select hides the chosen ones behind a scroll
                    box and loses the lot to one mis-aimed click. MultiSelectSearch is the portal's
                    existing control for this and already carries the styling. */}
                {!scoped && <label className="otx-env-filter">
                    <span>Environment</span>
                    <MultiSelectSearch
                        options={envOptions}
                        value={filters.environmentIds}
                        placeholder="All environments"
                        onChange={(environmentIds) => {
                            // Drop a hotel none of the chosen environments contains, rather than leaving
                            // a selection that is invisible in the list below and returns nothing.
                            const chosen = new Set(environmentIds);
                            const inEnv = new Set();
                            for (const env of (envs.environments || [])) {
                                if (!chosen.has(env.environmentId)) continue;
                                for (const c of (env.hotelCodes || [])) inEnv.add(c);
                            }
                            // A chain stays while any of its hotels is in them (it is narrowed to those when the
                            // codes are worked out); one with none left would be an invisible chip.
                            const keep = (k) => !inEnv.size
                                || codesOfKeys([k], options.hotelLabels).some(c => inEnv.has(c));
                            setFilters(f => ({ ...f, environmentIds, hotelKeys: (f.hotelKeys || []).filter(keep) }));
                        }}
                    />
                </label>}
                <label className="otx-env-filter">
                    <span>Hotel or chain</span>
                    <MultiSelectSearch
                        options={hotelOptions}
                        value={filters.hotelKeys}
                        placeholder="All hotels — search by name, code or chain"
                        onChange={(hotelKeys) => setFilters(f => ({ ...f, hotelKeys }))}
                    />
                </label>
                <label className="otx-check">
                    <input type="checkbox" checked={filters.failedOnly} onChange={e => setFilters(f => ({ ...f, failedOnly: e.target.checked }))} />
                    <span>Failures only</span>
                </label>
                <Button variant="secondary" onClick={search}><Search className="icon" /> Search</Button>
                {/* GENERATE INVOICE, for the environment currently filtered on.
                    Disabled without one: an invoice is raised for an environment, and "all environments"
                    is the wizard's job (Billing > Invoices > Invoice all), not a button that would quietly
                    bill everybody. The month comes from the From date rather than today, so filtering to
                    August and pressing it invoices August. */}
                {!scoped && <Button
                    variant="secondary"
                    disabled={(filters.environmentIds || []).length !== 1}
                    title={(filters.environmentIds || []).length === 1
                        ? 'Generate an invoice for this environment'
                        : (filters.environmentIds || []).length > 1
                            // An invoice is raised against ONE environment. Billing several in a single
                            // click would produce one document covering separate tenancies, which is not
                            // a thing anybody can send. Filtering to several to READ them is fine.
                            ? 'Invoicing is one environment at a time — narrow the filter to one, or use '
                              + 'Invoice all on the Invoices screen'
                            : 'Choose an environment first — or use Invoice all on the Invoices screen'}
                    onClick={() => {
                        const env = (envs.environments || []).find(e => e.environmentId === filters.environmentIds[0]);
                        setInvoicing({
                            hotelCodes: env ? env.hotelCodes || [] : [],
                            month: (filters.from || '').slice(0, 7),
                        });
                    }}
                >
                    <FileText className="icon" /> Generate invoice
                </Button>}
            </div>

            {error && (
                <div className="otx-error"><AlertCircle size={16} /> {error}</div>
            )}

            {invoicing && !scoped && (
                <InvoiceWizard
                    initialHotelCodes={invoicing.hotelCodes}
                    initialMonth={invoicing.month}
                    onClose={() => setInvoicing(null)}
                />
            )}

            {loading && !summary ? <Spinner /> : summary && (
                <>
                    <div className={`otx-stats${stale ? ' otx-stats-stale' : ''}`}>
                        <div className="otx-stat">
                            <span className="otx-stat-label">Total requests</span>
                            <span className="otx-stat-value">{num(summary.totals.totalRequests)}</span>
                        </div>
                        <div className="otx-stat">
                            <span className="otx-stat-label">Successful</span>
                            <span className="otx-stat-value otx-ok">{num(summary.totals.successRequests)}</span>
                        </div>
                        <div className="otx-stat">
                            <span className="otx-stat-label">Failed</span>
                            <span className="otx-stat-value otx-fail">{num(summary.totals.failedRequests)}</span>
                        </div>
                        {/* Failed split by HTTP class. Oracle Analytics' "Errors" counts server errors (5xx)
                            only; our Failed is its failureCount, which includes every 4xx (mostly 401 bad
                            tokens). The split comes from per-group error records, written only by the
                            daily job — so it may cover fewer days than the range, and says so. */}
                        {summary.errors && (() => {
                            const e = summary.errors;
                            const partial = e.days < summary.totals.days;
                            const note = [
                                `${num(e.days)} of ${num(summary.totals.days)} days have an error breakdown`
                                    + (partial ? ' (days before 2026-08-15 were loaded without one)' : ''),
                                e.scope === 'group' ? 'Counted per environment group — the hotel filter is widened to its groups' : null,
                                e.codes.length ? `By code: ${e.codes.map(c => `${c.code} × ${num(c.count)}`).join(', ')}` : null,
                            ].filter(Boolean).join('\n');
                            return (
                                <>
                                    <div className="otx-stat" title={note}>
                                        <span className="otx-stat-label">Server errors (5xx){partial ? ' *' : ''}</span>
                                        <span className="otx-stat-value otx-fail">{num(e.serverErrors)}</span>
                                    </div>
                                    <div className="otx-stat" title={note}>
                                        <span className="otx-stat-label">Rejected (4xx){partial ? ' *' : ''}</span>
                                        <span className="otx-stat-value">{num(e.clientErrors)}</span>
                                    </div>
                                </>
                            );
                        })()}
                        <div className="otx-stat">
                            <span className="otx-stat-label">Success rate</span>
                            <span className="otx-stat-value">{summary.totals.successRate == null ? '—' : `${summary.totals.successRate}%`}</span>
                        </div>
                        <div className="otx-stat">
                            <span className="otx-stat-label">Days / environments / hotels</span>
                            <span className="otx-stat-value otx-stat-small">
                                {num(summary.totals.days)} / {num(summary.totals.groups)} / {num(summary.totals.hotels)}
                            </span>
                        </div>
                    </div>

                    {/* Bars are sized against the busiest day in the CURRENT selection, so the shape of
                        the period is readable whatever its absolute volume. */}
                    <div className="otx-chart">
                        <div className="otx-chart-head">
                            <span>Requests per day</span>
                            {hasData && <span className="otx-chart-max">peak {num(peakDay)}</span>}
                        </div>
                        <div className="otx-chart-body">
                            <div className="otx-yaxis" aria-hidden="true">
                                {hasData && axis.ticks.map((t, i) => <span key={i} className="otx-ytick">{tick(t)}</span>)}
                            </div>
                            <div className="otx-plot">
                                {/* Gridlines sit behind the bars and share the axis's tick positions, so a
                                    bar's height can actually be read against a number. */}
                                <div className="otx-grid" aria-hidden="true">
                                    {axis.ticks.map((t, i) => <div key={i} className="otx-gridline" />)}
                                </div>
                                <div className="otx-bars">
                                    {(summary.byDay || []).map(d => (
                                        <div key={d.date} className="otx-bar-wrap"
                                             title={`${d.date}\n${num(d.totalRequests)} requests\n${num(d.failedRequests)} failed`}>
                                            <div className="otx-bar">
                                                <div className="otx-bar-fail" style={{ height: `${(d.failedRequests / axis.top) * 100}%` }} />
                                                <div className="otx-bar-ok" style={{ height: `${(d.successRequests / axis.top) * 100}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                    {!(summary.byDay || []).length && <div className="otx-empty">No data in this range.</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="otx-table-wrap">
                <table className="otx-table">
                    <thead>
                        <tr>
                            {[
                                ['date', 'Date'], ['group', 'Environment'], ['hotelId', 'Hotel'],
                                ['totalRequests', 'Total'], ['successRequests', 'Success'], ['failedRequests', 'Failed'],
                            ].map(([field, label]) => (
                                <th key={field}
                                    className={`otx-sortable${sort.field === field ? ' otx-sorted' : ''}`}
                                    onClick={() => toggleSort(field)}>
                                    {label}{sort.field === field ? (sort.order === 'asc' ? ' ▲' : ' ▼') : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6}><Spinner /></td></tr>}
                        {!loading && !rows.length && (
                            <tr><td colSpan={6} className="otx-empty">No transactions match these filters.</td></tr>
                        )}
                        {!loading && rows.map(r => (
                            <tr key={r._id}>
                                <td>{r.date}</td>
                                <td>{r.group}</td>
                                <td>
                                    {labelByCode.get(r.hotelId)?.hotelName
                                        ? <><span title={labelByCode.get(r.hotelId).chainName || ''}>{labelByCode.get(r.hotelId).hotelName}</span> <span className="otx-code">{r.hotelId}</span></>
                                        : r.hotelId}
                                    {/* Not a column of its own: environment is not a field on the
                                        document, so it cannot be sorted server-side, and a sortable
                                        header that silently does nothing is worse than none. */}
                                    {envByCode.get(r.hotelId) && (
                                        /* The TENANCY, not the environment id: the environment id is a
                                           32-char GUID that means nothing on a row, while "mtcu6pr" is
                                           what a gateway URL shows. The full environment name is the
                                           title — and for the 47 codes that live in a Production and a
                                           UAT environment it is the Production one, because the document
                                           itself carries no environment to choose by. */
                                        <span className="otx-env" title={envByCode.get(r.hotelId).environmentName}>
                                            {envByCode.get(r.hotelId).tenancyId}
                                        </span>
                                    )}
                                </td>
                                <td className="otx-numeric">{num(r.totalRequests)}</td>
                                <td className="otx-numeric otx-ok">{num(r.successRequests)}</td>
                                <td className={`otx-numeric${r.failedRequests ? ' otx-fail' : ''}`}>{num(r.failedRequests)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="otx-pager">
                    <Button variant="secondary" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Previous</Button>
                    <span className="otx-pager-info">
                        Page {page + 1} of {num(totalPages)} — {num(total)} rows
                    </span>
                    <Button variant="secondary" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</Button>
                </div>
            )}
        </div>
    );
}

export default OhipTransactionsPage;
